import { segments } from "./scale";

/**
 * A small trend line for a stat tile (dataviz: the trend of a figure): one series, no axes, a
 * dot on the last value. Its label names the first and last value; the tile shows the number.
 */
export function Sparkline({
  points,
  label,
  width = 120,
  height = 32,
}: {
  points: readonly (number | null)[];
  label: string;
  width?: number;
  height?: number;
}) {
  const values = points.filter((p): p is number => p !== null && Number.isFinite(p));
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = 4;
  const x = (i: number) => pad + (i * (width - 2 * pad)) / Math.max(1, points.length - 1);
  const y = (v: number) =>
    max === min ? height / 2 : height - pad - ((v - min) / (max - min)) * (height - 2 * pad);
  const last = points.length - 1 - [...points].reverse().findIndex((p) => p !== null);
  const lastValue = points[last];
  return (
    <svg role="img" aria-label={label} width={width} height={height} className="overflow-visible">
      {segments(points).map((run) => (
        <polyline
          key={run.start}
          points={run.values.map((v, i) => `${x(run.start + i)},${y(v)}`).join(" ")}
          fill="none"
          stroke="var(--chart-1)"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ))}
      {typeof lastValue === "number" ? (
        <circle
          cx={x(last)}
          cy={y(lastValue)}
          r={4}
          fill="var(--chart-1)"
          stroke="var(--surface)"
          strokeWidth={2}
        />
      ) : null}
    </svg>
  );
}
